import { forwardRef, useImperativeHandle } from 'react';
import React, { useState, useEffect, useRef } from 'react';
import useUploadContext from "hooks/useUploadContext";
import "./index.scss";

const Waveform = forwardRef(function Waveform({ file, waveLength, color = 'white', updateLoading }, ref) {
    const {
        totalTime,
        leftBoundary,
        rightBoundary,
        setLayersUpdated,
        audioContextRef
    } = useUploadContext();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const waveformDivRef = useRef(null);
    const svgRef = useRef(null);

    const h = waveformDivRef.current?.clientHeight || 0;
    const w = waveformDivRef.current?.clientWidth || 0;

    useImperativeHandle(ref, () => ({
        updateLoading: (newLoading) => {
            setLoading(newLoading);
        },
        loading,
    }));

    useEffect(()=>{
        if(!!file) {
            updateLoading()
        }
    }, [file, loading, updateLoading])

    useEffect(() => {
        const setFile = async() => {
            if (!file) {
                setData([]);
                return
            };
            setLoading(true);
            const audioContext = audioContextRef?.current;
            if(!audioContext) return;
            const arrayBuffer = await file.blob.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            
            let audioData = [];
            if (audioBuffer.numberOfChannels === 2) {
                const channel1Data = audioBuffer.getChannelData(0);
                const channel2Data = audioBuffer.getChannelData(1);
                const length = channel1Data.length;
                for (let i = 0; i < length; i++) {
                    audioData[i] = (channel1Data[i] + channel2Data[i]) / 2;
                }
            }
            setData(audioData);
            setLoading(false);
            setLayersUpdated(true);
        };
        setFile();
    }, [file, setLayersUpdated, audioContextRef]);

    useEffect(() => {
        const svg = svgRef.current;
        svg.innerHTML = '';
        if (!data.length) {
            return
        }
        if (data.length) {
            const svg = svgRef.current;
            svg.innerHTML = '';
            const numberOfLines = Math.floor(w * waveLength/totalTime);
            const amp = h / 2;
            let left = leftBoundary >= waveLength ? waveLength : leftBoundary;
            let right = rightBoundary >= waveLength ?  waveLength : rightBoundary;
            const selectedData = data.slice(Math.floor(data.length * left / totalTime), Math.floor(data.length * right / totalTime));
            const step = Math.ceil(selectedData.length / numberOfLines);
            for (let i = 0; i < numberOfLines - 1; i++) {
                let [min, max] = [1, -1];

                for (let j = 0; j < step; j++) {
                    const datum = selectedData[(i * step) + j];
                    if (datum < min) min = datum;
                    if (datum > max) max = datum;
                }

                const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
                line.setAttribute("x1", i);
                line.setAttribute("y1", (1 + min) * amp);
                line.setAttribute("x2", i);
                line.setAttribute("y2", (1 + max) * amp);
                line.setAttribute("stroke", color);
                line.setAttribute("strokeWidth", "1");
                svg.appendChild(line);
            }
        }
    }, [data, w, h, color, leftBoundary, rightBoundary, totalTime, waveLength]);

    return (
        <div ref={waveformDivRef} className={`waveform-component ${loading ? 'skeleton' : ''}`}>
            <svg
                ref={svgRef}
                width={w}
                height={h}
            />
        </div>
    );
});

export default Waveform;
